(() => {
  'use strict';

  const selector = '[data-alden-size-guide]';
  const cache = window.__aldenSizeChart = window.__aldenSizeChart || {};

  const setFrameHidden = (frame) => {
    frame.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;height:600px;border:0;pointer-events:none;';
  };

  const setFrameVisible = (frame) => {
    frame.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;border:0;z-index:99999;background:transparent;pointer-events:auto;';
  };

  const revealButtons = (entry) => {
    entry.buttons.forEach((button) => {
      if (button.isConnected) button.style.removeProperty('display');
    });
  };

  const watchForClose = (entry, hasOpened = false, attempts = 0) => {
    const poll = () => {
      if (!entry.frame?.isConnected) return;

      if (entry.win?.SmartRecomSizeChart?.isModalOpen?.()) {
        watchForClose(entry, true, attempts + 1);
      } else if (!hasOpened && attempts < 20) {
        watchForClose(entry, false, attempts + 1);
      } else {
        setFrameHidden(entry.frame);
      }
    };

    window.setTimeout(poll, 250);
  };

  const frameTranslations = {
    nl: {
      'Close': 'Sluiten',
      'Size Recommendation': 'Maatadvies',
      'Height / Weight': 'Lengte / gewicht',
      'Between sizes?': 'Tussen twee maten?',
      'Size up for relaxed, down for fitted.': 'Kies groter voor een lossere fit en kleiner voor een aangesloten fit.'
    },
    de: {
      'Close': 'Schließen',
      'Size Recommendation': 'Größenhilfe',
      'Height / Weight': 'Größe / Gewicht',
      'Between sizes?': 'Zwischen zwei Größen?',
      'Size up for relaxed, down for fitted.': 'Lockerer Sitz: größer. Enger Sitz: kleiner.'
    }
  };

  const localizeFrame = (frame) => {
    // The storefront can keep the source language on <html> while LangShop
    // serves a translated locale. Prefer the server-rendered request locale.
    const language = (frame.dataset.locale || document.documentElement.lang || '')
      .toLowerCase()
      .split('-')[0];
    const translations = frameTranslations[language];
    const frameDocument = frame.contentDocument;
    if (!translations || !frameDocument?.body) return;

    const observedRoots = frame.__aldenObservedRoots ||= new WeakSet();

    const translate = (root) => {
      const walker = frameDocument.createTreeWalker(
        root,
        frameDocument.defaultView.NodeFilter.SHOW_TEXT
      );
      let node = walker.nextNode();

      while (node) {
        const original = node.nodeValue;
        const trimmed = original.trim();
        if (translations[trimmed]) node.nodeValue = original.replace(trimmed, translations[trimmed]);
        node = walker.nextNode();
      }

      root.querySelectorAll?.('[aria-label], [title]').forEach((element) => {
        ['aria-label', 'title'].forEach((attribute) => {
          const value = element.getAttribute(attribute);
          if (translations[value]) element.setAttribute(attribute, translations[value]);
        });
      });

      root.querySelectorAll?.('*').forEach((element) => {
        if (element.shadowRoot) translate(element.shadowRoot);
      });

      if (observedRoots.has(root)) return;
      observedRoots.add(root);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'characterData') translate(mutation.target.parentElement);
          mutation.addedNodes.forEach((addedNode) => {
            if (addedNode.nodeType === Node.TEXT_NODE) translate(addedNode.parentElement);
            if (addedNode.nodeType === Node.ELEMENT_NODE) translate(addedNode);
          });
        });
      });
      observer.observe(root, { childList: true, characterData: true, subtree: true });
    };

    translate(frameDocument.body);
  };

  const localizeFrameRepeatedly = (frame, scans = 12) => {
    let remainingScans = scans;
    const timer = window.setInterval(() => {
      localizeFrame(frame);
      remainingScans -= 1;
      if (remainingScans === 0 || !frame.isConnected) window.clearInterval(timer);
    }, 250);
  };

  const openEntry = (entry) => {
    if (entry.status !== 'available' || !entry.frame?.isConnected || !entry.win?.SmartRecomSizeChart) return;

    localizeFrame(entry.frame);
    localizeFrameRepeatedly(entry.frame);
    setFrameVisible(entry.frame);
    if (!entry.win.SmartRecomSizeChart.isModalOpen?.()) {
      try {
        entry.win.SmartRecomSizeChart.openModal();
      } catch (error) {
        setFrameHidden(entry.frame);
        return;
      }
    }

    watchForClose(entry);
  };

  const findDependencies = (callback, attempt = 1) => {
    const config = document.getElementById('smartrecom-sizechart-config');
    const bundle = Array.from(document.scripts).find((script) => script.src.includes('smartsize-sizechart-main.js'));

    if (config && bundle?.src) {
      callback(config, bundle.src);
    } else if (attempt < 40) {
      window.setTimeout(() => findDependencies(callback, attempt + 1), 250);
    } else {
      callback(null, null);
    }
  };

  const copyWidgetConfig = (frameDocument, productId, sourceConfig) => {
    const config = frameDocument.createElement('div');
    Array.from(sourceConfig.attributes).forEach(({ name, value }) => {
      if (name !== 'id' && name !== 'style') config.setAttribute(name, value);
    });
    config.id = 'smartrecom-sizechart-config';
    config.dataset.smartrecomProductId = productId;
    config.dataset.smartrecomIsProductPage = 'true';
    config.style.display = 'none';
    frameDocument.body.appendChild(config);

    const sourceSettings = document.getElementById('smartrecom-sizechart-settings');
    if (!sourceSettings) return;

    const settings = frameDocument.createElement('script');
    settings.type = 'application/json';
    settings.id = 'smartrecom-sizechart-settings';
    settings.textContent = sourceSettings.textContent;
    frameDocument.body.appendChild(settings);
  };

  const probeProduct = (entry, productId, sourceConfig, bundleSource) => {
    const frame = document.createElement('iframe');
    frame.id = `alden-size-guide-frame-${productId}`;
    frame.title = 'Size guide';
    frame.dataset.locale = entry.locale || '';
    setFrameHidden(frame);
    document.body.appendChild(frame);

    const frameDocument = frame.contentDocument;
    if (!frameDocument?.head || !frameDocument.body) {
      entry.status = 'unavailable';
      frame.remove();
      return;
    }

    const style = frameDocument.createElement('style');
    style.textContent = 'html,body{margin:0;padding:0;background:transparent;}';
    frameDocument.head.appendChild(style);
    copyWidgetConfig(frameDocument, productId, sourceConfig);

    const bundle = frameDocument.createElement('script');
    bundle.src = bundleSource;
    bundle.onload = () => {
      const frameWindow = frame.contentWindow;
      let attempts = 0;

      const detectChart = () => {
        attempts += 1;
        try {
          frameWindow?.SmartRecomSizeChart?.openModal?.();
        } catch (error) {
          // The availability loop below handles a widget that is not ready yet.
        }

        if (frameWindow?.SmartRecomSizeChart?.isModalOpen?.()) {
          localizeFrame(frame);
          localizeFrameRepeatedly(frame);
          entry.status = 'available';
          entry.frame = frame;
          entry.win = frameWindow;
          revealButtons(entry);
        } else if (attempts < 25) {
          window.setTimeout(detectChart, 250);
        } else {
          entry.status = 'unavailable';
          frame.remove();
        }
      };

      detectChart();
    };
    frameDocument.body.appendChild(bundle);
  };

  const initializeButton = (button) => {
    if (!button) return;
    const productId = button.dataset.productId;
    if (!productId) return;

    let entry = button.__aldenSizeGuideEntry || cache[productId];
    if (entry) {
      button.dataset.aldenSizeGuideReady = 'true';
      entry.buttons = entry.buttons || new Set();
      entry.buttons.add(button);
      button.__aldenSizeGuideEntry = entry;
      if (entry.status === 'available') revealButtons(entry);
      return;
    }

    // Product sections can clone the button after a variant/bundle update.
    // Dataset attributes survive that clone, but JS properties do not.
    button.dataset.aldenSizeGuideReady = 'true';
    button.style.display = 'none';

    entry = cache[productId] = {
      status: 'pending',
      frame: null,
      win: null,
      locale: button.dataset.locale,
      buttons: new Set([button])
    };
    button.__aldenSizeGuideEntry = entry;

    findDependencies((sourceConfig, bundleSource) => {
      if (!sourceConfig || !bundleSource) {
        entry.status = 'unavailable';
        return;
      }

      probeProduct(entry, productId, sourceConfig, bundleSource);
    });
  };

  const initializeButtons = (root = document) => {
    if (root.matches?.(selector)) initializeButton(root);
    root.querySelectorAll?.(selector).forEach(initializeButton);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(selector);
    if (!button) return;

    const entry = button.__aldenSizeGuideEntry || cache[button.dataset.productId];
    if (entry?.status === 'available') openEntry(entry);
  }, true);

  initializeButtons();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initializeButtons(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
